/**
 * The URLs the homework loop's pages hand each other, built in one place so
 * the homework page, the class page links and the Save as PDF preview agree
 * on the parameter names (`class`, `homework`, `from`, `open`, `new`).
 *
 * The homework page and the preview are each reached from several surfaces,
 * so their back link comes from an explicit, validated origin
 * (`@/utils/teacherOrigin`), never from who the viewer is. `?new=<classCodeId>`
 * opens the homework page's New homework dialog on that class, for the class
 * page's "Assign homework". `?back=results` marks a preview opened from the
 * results: its origin is the results' own, handed back to them.
 */

import { TEACHER_PAGE_RETURN_STATE } from '@/utils/teacherEventSource';
import {
  appendTeacherOrigin,
  parseTeacherOrigin,
  type StudentOrigin,
  type TeacherOrigin,
  teacherOriginHref,
} from '@/utils/teacherOrigin';

/**
 * The homework page, opened on this homework. With an origin, the page's
 * back affordance returns there instead of to the homework list.
 */
export function homeworkPageHref(
  homeworkId: string,
  origin?: TeacherOrigin | null
): string {
  const query = new URLSearchParams({ open: homeworkId });
  if (origin) appendTeacherOrigin(query, origin);
  return `/teacher/homework?${query.toString()}`;
}

/** The homework page with its New homework dialog open on this class. */
export function newHomeworkHref(
  classCodeId: string,
  origin?: TeacherOrigin | null
): string {
  const query = new URLSearchParams({ new: classCodeId });
  if (origin) appendTeacherOrigin(query, origin);
  return `/teacher/homework?${query.toString()}`;
}

/**
 * The New homework dialog on the student's class, "For" set to them, with
 * the builder's back link returning to their page in the view the teacher
 * left (its homework, window and tab).
 */
export function newStudentHomeworkHref(origin: StudentOrigin): string {
  const query = new URLSearchParams({
    new: origin.classCodeId,
    student: origin.studentUserId,
  });
  appendTeacherOrigin(query, origin);
  return `/teacher/homework?${query.toString()}`;
}

/**
 * The Save as PDF preview of one homework's questions, opened from its
 * results. The origin is the results' own way back, handed back to them on
 * "Back to results". (The class page builds its own preview link, with
 * `homeworkPrintUrl` in `studentsUrls.ts`.)
 */
export function homeworkPrintHref(
  homeworkId: string,
  origin?: TeacherOrigin | null
): string {
  // The results' origin takes `from=`, so the hop is marked apart from it.
  const query = new URLSearchParams({ back: 'results' });
  if (origin) appendTeacherOrigin(query, origin);
  return `/teacher/homework/${encodeURIComponent(homeworkId)}/print?${query.toString()}`;
}

export interface ClassPageReturn {
  to: string;
  /** Router state: back to the class page is a return, not a new open. */
  state: typeof TEACHER_PAGE_RETURN_STATE;
}

/**
 * The class page a `from=students` origin returns to, with its homework in
 * view, window and tab, or null when there is no origin or it is malformed
 * (a stale or hand-edited link): only a well-formed class and homework in
 * view earn a "Back to class".
 */
export function classPageReturn(
  search: URLSearchParams
): ClassPageReturn | null {
  const origin = parseTeacherOrigin(search);
  if (origin?.kind !== 'class' || !origin.homeworkId) return null;
  return { to: teacherOriginHref(origin), state: TEACHER_PAGE_RETURN_STATE };
}

export interface HomeworkPrintBackLink {
  label: 'Back to class' | 'Back to results';
  to: string;
  state?: typeof TEACHER_PAGE_RETURN_STATE;
}

/**
 * The preview's back link. Opened from the results (`back=results`), it
 * returns there with the results' own origin, so their "Back to class",
 * "Back to {name}" or "Back to worksheet" survives the trip. Opened from the
 * class page (a well-formed `from=students`), it returns to the class page.
 * Anything else returns to the results.
 */
export function homeworkPrintBackLink(
  homeworkId: string,
  search: URLSearchParams
): HomeworkPrintBackLink {
  const classPage =
    search.get('back') === 'results' ? null : classPageReturn(search);
  if (classPage) return { label: 'Back to class', ...classPage };
  return {
    label: 'Back to results',
    to: homeworkPageHref(homeworkId, parseTeacherOrigin(search)),
  };
}
